/**
 * Login — email + password entry against the Cognito user pool.
 * Handles three flows in-page:
 *
 *   1. Normal sign-in → redirect to the page the user was trying to reach.
 *   2. First-time login: Cognito returns NEW_PASSWORD_REQUIRED for
 *      admin-created users with temporary passwords. We swap to a
 *      "set permanent password" phase and finish the challenge in place.
 *   3. PasswordResetRequired (the DM reset the account) → hand straight
 *      over to /forgot-password with the email prefilled and a code sent.
 *
 * Every failure is mapped through authErrors.ts so the player sees what
 * happened and what to do next (Dev Handoff 2026-09-07 — two of three
 * players locked out at session start; one thought he needed a new
 * account). Nothing on this page ever suggests creating an account.
 */
import { FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import type { AuthUser } from '../lib/auth';
import {
  TEMP_PASSWORD_DAYS,
  describeAuthError,
  normalizeEmail,
  type AuthErrorInfo,
} from '../lib/authErrors';
import { Button, Card, Field, Loading, TextInput } from '../components/ui';
import { AuthAlert, AuthErrorAlert, PasswordInput } from './authWidgets';
import './auth.css';

interface LocationState {
  from?: { pathname: string };
  /** One-line success notice, e.g. after a password reset. */
  notice?: string;
  /** Prefill the email field (set by ForgotPassword on success). */
  email?: string;
}

type Phase =
  | { kind: 'credentials' }
  | {
      kind: 'newPassword';
      complete: (newPassword: string) => Promise<AuthUser>;
    };

export default function Login() {
  const { user, signIn, refresh, loading, sessionNotice, resendConfirmationCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const [phase, setPhase] = useState<Phase>({ kind: 'credentials' });
  // The AdminUsers lambda invites players with their email as the Cognito
  // username, so email is what we collect. auth.ts trims and retries
  // lowercased if the typed form is rejected.
  const [email, setEmail] = useState(locationState?.email ?? '');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState<AuthErrorInfo | null>(null);
  const [wrongPasswordCount, setWrongPasswordCount] = useState(0);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="auth-page">
        <Loading label="Checking your session…" />
      </div>
    );
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const redirectTo = locationState?.from?.pathname ?? '/dashboard';
  const notice = locationState?.notice ?? null;

  async function onSubmitCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const result = await signIn(email, password);
      if (result.kind === 'success') {
        navigate(redirectTo, { replace: true });
        return;
      }
      // First-time login challenge — hold the in-flight cognito session
      // in component state and swap to the new-password form.
      setPhase({ kind: 'newPassword', complete: result.complete });
    } catch (err) {
      const mapped = describeAuthError(err, 'signIn');
      if (mapped.action === 'goToReset') {
        // The DM reset this account: the only way forward is a code by
        // email. Send it now and land on the code form, no extra clicks.
        navigate('/forgot-password', {
          replace: false,
          state: { email: normalizeEmail(email), autoSend: true, reason: 'resetRequired' },
        });
        return;
      }
      if (mapped.action === 'forgotPassword' && mapped.code === 'NotAuthorizedException') {
        setWrongPasswordCount((n) => n + 1);
      }
      setError(mapped);
    } finally {
      setSubmitting(false);
    }
  }

  async function onResendConfirmation() {
    setError(null);
    setSubmitting(true);
    try {
      await resendConfirmationCode(email);
      setInfo(`A new confirmation code has been emailed to ${normalizeEmail(email)}.`);
    } catch (err) {
      setError(describeAuthError(err, 'requestReset'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitNewPassword(e: FormEvent) {
    e.preventDefault();
    if (phase.kind !== 'newPassword') return;
    setError(null);

    if (newPassword !== confirmNewPassword) {
      setError({
        code: 'Mismatch',
        title: 'Those passwords do not match',
        detail: 'Type the same new password in both boxes — use Show to check.',
      });
      return;
    }

    setSubmitting(true);
    try {
      await phase.complete(newPassword);
      // The cognito-identity-js library persists the session once the
      // challenge completes, so refresh() picks it up and fills the
      // AuthContext user. Then we can navigate as normal.
      await refresh();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(describeAuthError(err, 'newPassword'));
    } finally {
      setSubmitting(false);
    }
  }

  function backToCredentials() {
    setPhase({ kind: 'credentials' });
    setPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setError(null);
  }

  const repeatedWrongPassword = wrongPasswordCount >= 2;

  return (
    <div className="auth-page">
      <Card className="auth-card">
        <h1 className="auth-title">GoobDoob VTT</h1>
        <p className="auth-subtitle">
          {phase.kind === 'credentials' ? 'Sign in with the email the DM invited you with.' : 'First sign-in'}
        </p>

        {notice && <AuthAlert tone="success" title={notice} />}

        {sessionNotice && phase.kind === 'credentials' && (
          <AuthAlert
            tone={sessionNotice.kind === 'offline' ? 'warning' : 'info'}
            title={sessionNotice.title}
            detail={sessionNotice.detail}
            actions={
              sessionNotice.kind === 'offline' ? (
                <Button type="button" size="sm" variant="secondary" onClick={() => void refresh()}>
                  Retry
                </Button>
              ) : undefined
            }
          />
        )}

        {info && <AuthAlert tone="success" title={info} />}

        {phase.kind === 'credentials' ? (
          <>
            <form onSubmit={onSubmitCredentials} className="auth-form" style={{ marginTop: 'var(--sp-4)' }}>
              <Field label="Email" required>
                <TextInput
                  type="email"
                  name="email"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus={!email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>
              <Field label="Password" required>
                <PasswordInput
                  name="password"
                  autoComplete="current-password"
                  autoFocus={!!email}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>

              {error && (
                <AuthErrorAlert
                  info={error}
                  email={normalizeEmail(email)}
                  emphasizeReset={repeatedWrongPassword}
                  onResendCode={onResendConfirmation}
                  onRetry={() => setError(null)}
                  extra={
                    repeatedWrongPassword && error.action === 'forgotPassword'
                      ? `That is ${wrongPasswordCount} wrong tries — resetting now takes about a minute and avoids a lockout.`
                      : undefined
                  }
                />
              )}

              <Button type="submit" size="lg" block loading={submitting}>
                Sign in
              </Button>
            </form>

            {/*
              Forgot-password surface — bug 2026-05-11 #8: Audrey couldn't get
              back in and didn't notice this link. Keep it a full-width,
              visually distinct button beneath the form as well as inline
              with any error text above.
            */}
            <Link
              to="/forgot-password"
              state={{ email: normalizeEmail(email) }}
              className="ui-btn ui-btn--ghost ui-btn--block"
              style={{ marginTop: 'var(--sp-4)' }}
            >
              Forgot your password?
            </Link>

            <p className="auth-help">
              <strong>New here?</strong> Your first sign-in uses the temporary password from the
              DM's invite email (sender <strong>no-reply@verificationemail.com</strong> — check
              spam). It only works for {TEMP_PASSWORD_DAYS} days; if it has expired or the email is
              gone, the DM can resend it in a click. You never need to create an account yourself.
            </p>
          </>
        ) : (
          <form onSubmit={onSubmitNewPassword} className="auth-form" style={{ marginTop: 'var(--sp-4)' }}>
            <p className="auth-lead">
              Welcome! Your temporary password worked — now choose a permanent one for{' '}
              <strong>{normalizeEmail(email)}</strong>.
            </p>
            <Field
              label="New password"
              required
              hint="At least 8 characters. The pool may also require upper and lower case, a number and a symbol — we will tell you exactly what is missing."
            >
              <PasswordInput
                autoComplete="new-password"
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </Field>
            <Field label="Confirm new password" required>
              <PasswordInput
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </Field>
            {error && <AuthErrorAlert info={error} onSignIn={backToCredentials} onRetry={() => setError(null)} />}
            <Button type="submit" size="lg" block loading={submitting}>
              Set password and continue
            </Button>
            <Button type="button" variant="subtle" onClick={backToCredentials}>
              Start over
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
